import { useState, useEffect, useCallback } from "react";
import { useAccount, useWalletClient, useWriteContract, useWaitForTransactionReceipt } from "wagmi";
import { useToast } from "@/components/ui/use-toast";
import { parseAbiItem, Address, Hash, encodeAbiParameters, keccak256, parseAbiParameters, toBytes } from "viem";
import { polygon } from "viem/chains";
import { polygonArchiveRpcClients, polygonRpcClients } from "@/lib/polygonRpc";
import { useNetworkSwitch } from "@/components/create-coin/form/hooks/useNetworkSwitch";
import { getDescendingBlockRanges, getHistoryStartBlock, getLogsWithAdaptiveRange, waitForNextLogBatch } from "../utils/chainHistory";

const GOVERNOR_ABI = [
  parseAbiItem("error GovernorInsufficientProposerVotes(address proposer, uint256 votes, uint256 threshold)"),
  parseAbiItem("error GovernorInvalidProposalLength(uint256 targets, uint256 calldatas, uint256 values)"),
  parseAbiItem("error GovernorNonexistentProposal(uint256 proposalId)"),
  parseAbiItem("error GovernorRestrictedProposer(address proposer)"),
  parseAbiItem("error GovernorUnexpectedProposalState(uint256 proposalId, uint8 current, bytes32 expectedStates)"),
  parseAbiItem("function state(uint256 proposalId) external view returns (uint8)"),
  parseAbiItem("function proposalVotes(uint256 proposalId) external view returns (uint256 againstVotes, uint256 forVotes, uint256 abstainVotes)"),
  parseAbiItem("function propose(address[] memory targets, uint256[] memory values, bytes[] memory calldatas, string memory description) external returns (uint256 proposalId)"),
  parseAbiItem("function castVote(uint256 proposalId, uint8 support) external returns (uint256 balance)"),
  parseAbiItem("function queue(address[] memory targets, uint256[] memory values, bytes[] memory calldatas, bytes32 descriptionHash) external returns (uint256 proposalId)"),
  parseAbiItem("function execute(address[] memory targets, uint256[] memory values, bytes[] memory calldatas, bytes32 descriptionHash) external payable returns (uint256 proposalId)"),
  parseAbiItem("function proposalNeedsQueuing(uint256 proposalId) external view returns (bool)"),
  parseAbiItem("function proposalEta(uint256 proposalId) external view returns (uint256)"),
  parseAbiItem("function proposalThreshold() external view returns (uint256)"),
  parseAbiItem("function token() external view returns (address)"),
  parseAbiItem("function clock() external view returns (uint48)"),
  parseAbiItem("function votingDelay() external view returns (uint256)"),
  parseAbiItem("function votingPeriod() external view returns (uint256)"),
] as const;

const GOVERNANCE_TOKEN_ABI = [
  parseAbiItem("function balanceOf(address account) external view returns (uint256)"),
  parseAbiItem("function delegates(address account) external view returns (address)"),
  parseAbiItem("function getVotes(address account) external view returns (uint256)"),
  parseAbiItem("function getPastVotes(address account, uint256 timepoint) external view returns (uint256)"),
  parseAbiItem("function clock() external view returns (uint48)"),
  parseAbiItem("function delegate(address delegatee) external"),
] as const;

const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";
const polygonPublicClient = polygonRpcClients[0];
// A one-operation proposal is normally well below this. Supplying a limit
// prevents wallet RPCs from repeating an unreliable eth_estimateGas after the
// proposal has already passed simulation through the app's Polygon client.
const PROPOSAL_GAS_LIMIT = 500_000n;

interface ProposalCreatedLog {
  blockNumber: bigint | null;
  logIndex: number | null;
  args: {
    proposalId: bigint;
    proposer?: Address;
    targets?: readonly Address[];
    values?: readonly bigint[];
    calldatas?: readonly `0x${string}`[];
    description?: string;
    voteStart?: bigint;
    voteEnd?: bigint;
  };
}

const getErrorMessage = (error: unknown, fallback: string) => {
  if (!error || typeof error !== "object") return fallback;
  const candidate = error as { shortMessage?: string; message?: string };
  return candidate.shortMessage || candidate.message || fallback;
};

const isContractExecutionRevert = (error: unknown) => {
  let current = error;
  for (let depth = 0; depth < 10 && current && typeof current === "object"; depth += 1) {
    const candidate = current as { name?: string; cause?: unknown };
    if (candidate.name === "ContractFunctionRevertedError" || candidate.name === "ExecutionRevertedError") {
      return true;
    }
    current = candidate.cause;
  }
  return false;
};

const PROPOSAL_CREATED_EVENT = parseAbiItem(
  // The deployed Governor ABI emits every ProposalCreated field as
  // non-indexed. Keeping indexed flags here causes viem to decode the log
  // incorrectly and makes valid proposals appear to be missing.
  "event ProposalCreated(uint256 proposalId, address proposer, address[] targets, uint256[] values, string[] signatures, bytes[] calldatas, uint256 voteStart, uint256 voteEnd, string description)"
);
const getTimedOutTransactionHash = (error: unknown): Hash | null => {
  let current = error;

  for (let depth = 0; depth < 6 && current && typeof current === "object"; depth += 1) {
    const candidate = current as { name?: string; message?: string; cause?: unknown };
    const isReceiptTimeout = candidate.name === "WaitForTransactionReceiptTimeoutError"
      || candidate.message?.includes("Timed out while waiting for transaction");
    const hash = candidate.message?.match(/0x[a-fA-F0-9]{64}/)?.[0];

    if (isReceiptTimeout && hash) return hash as Hash;
    current = candidate.cause;
  }

  return null;
};

const submitWithTimeoutRecovery = async (
  submit: () => Promise<Hash>,
  transactionName: string,
): Promise<Hash> => {
  try {
    return await submit();
  } catch (error) {
    const submittedHash = getTimedOutTransactionHash(error);
    if (!submittedHash) throw error;

    // Some wallet connectors wait for a receipt internally and reject on an
    // RPC timeout even though eth_sendTransaction already returned a hash.
    console.warn(`${transactionName} transaction submitted; connector confirmation timed out:`, submittedHash);
    return submittedHash;
  }
};

const waitForBlockAfter = async (
  client: { getBlockNumber: (parameters?: { cacheTime?: number }) => Promise<bigint> },
  blockNumber: bigint,
  timeout = 45_000,
) => {
  const deadline = Date.now() + timeout;
  while (await client.getBlockNumber({ cacheTime: 0 }) <= blockNumber) {
    if (Date.now() >= deadline) {
      throw new Error("Delegation succeeded, but Polygon did not produce the next block in time. Retry the proposal now; you will not need to delegate again.");
    }
    await new Promise((resolve) => setTimeout(resolve, 1_000));
  }
};

export type ProposalState = 
  | "Pending"
  | "Active" 
  | "Canceled"
  | "Defeated"
  | "Succeeded"
  | "Queued"
  | "Expired"
  | "Executed";

export interface Proposal {
  id: string;
  proposer: string;
  description: string;
  state: ProposalState;
  forVotes: string;
  againstVotes: string;
  abstainVotes: string;
  startBlock: string;
  endBlock: string;
  targets: Address[];
  values: bigint[];
  calldatas: `0x${string}`[];
  needsQueuing: boolean;
  eta: string | null;
  executed: boolean;
  canceled: boolean;
}

export interface PendingProposalTransaction {
  hash: Hash;
  title: string;
  submittedAt: number;
  stage: "delegation" | "proposal";
}

export type ProposalLifecycleAction = "queue" | "execute";

export interface ActiveProposalLifecycleAction {
  proposalId: string;
  action: ProposalLifecycleAction;
}

const getPendingProposalStorageKey = (governorAddress: Address) =>
  `pending-dao-proposals:${governorAddress.toLowerCase()}`;

const readPendingProposalTransactions = (governorAddress: Address): PendingProposalTransaction[] => {
  if (typeof window === "undefined") return [];
  try {
    const stored = JSON.parse(window.localStorage.getItem(getPendingProposalStorageKey(governorAddress)) || "[]") as PendingProposalTransaction[];
    return stored
      .filter((transaction) => /^0x[a-fA-F0-9]{64}$/.test(transaction.hash))
      .map((transaction) => ({ ...transaction, stage: transaction.stage === "delegation" ? "delegation" : "proposal" }));
  } catch {
    return [];
  }
};

const PROPOSAL_STATES: ProposalState[] = [
  "Pending",
  "Active",
  "Canceled",
  "Defeated",
  "Succeeded",
  "Queued",
  "Expired",
  "Executed",
];

export const useGovernorProposals = (governorAddress?: Address, createdAt?: string) => {
  const publicClient = polygonPublicClient;
  const { data: walletClient } = useWalletClient();
  const { address, isConnected, connector, chainId: connectedChainId } = useAccount();
  const { ensureCorrectNetwork } = useNetworkSwitch();
  const { toast } = useToast();
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [isLoadingProposals, setIsLoadingProposals] = useState(false);
  const [proposalError, setProposalError] = useState<string | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [pendingProposalTransactions, setPendingProposalTransactions] = useState<PendingProposalTransaction[]>([]);

  const verifyPolygonWalletChain = useCallback(async () => {
    let actualChainId = walletClient ? await walletClient.getChainId() : connectedChainId;
    if (connector) {
      const provider = await connector.getProvider() as {
        request?: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
      };
      if (provider.request) {
        const providerChainId = await provider.request({ method: "eth_chainId" });
        if (typeof providerChainId === "string" || typeof providerChainId === "number") {
          actualChainId = Number(providerChainId);
        }
      }
    }
    if (actualChainId === undefined) throw new Error("Unable to verify the wallet network. Reconnect the wallet and try again.");
    if (actualChainId !== polygon.id) {
      throw new Error(`Wallet is connected to chain ${actualChainId}, not Polygon (137). No transaction was submitted.`);
    }
  }, [connectedChainId, connector, walletClient]);

  const updatePendingProposalTransactions = useCallback((
    update: (current: PendingProposalTransaction[]) => PendingProposalTransaction[],
  ) => {
    setPendingProposalTransactions((current) => {
      const transactions = update(current);
      if (governorAddress && typeof window !== "undefined") {
        window.localStorage.setItem(getPendingProposalStorageKey(governorAddress), JSON.stringify(transactions));
      }
      return transactions;
    });
  }, [governorAddress]);

  const upsertPendingProposalTransaction = useCallback((transaction: PendingProposalTransaction) => {
    updatePendingProposalTransactions((current) => [
      transaction,
      ...current.filter((item) => item.hash !== transaction.hash),
    ]);
  }, [updatePendingProposalTransactions]);

  const removePendingProposalTransaction = useCallback((hash: Hash) => {
    updatePendingProposalTransactions((current) => current.filter((transaction) => transaction.hash !== hash));
  }, [updatePendingProposalTransactions]);

  useEffect(() => {
    setPendingProposalTransactions(governorAddress ? readPendingProposalTransactions(governorAddress) : []);
  }, [governorAddress]);

  useEffect(() => {
    if (!publicClient || pendingProposalTransactions.length === 0) return;
    let cancelled = false;

    const checkPendingTransactions = async () => {
      const settled = await Promise.all(pendingProposalTransactions.map(async (transaction) => {
        try {
          const receipt = await publicClient.getTransactionReceipt({ hash: transaction.hash });
          return { transaction, status: receipt.status };
        } catch {
          return null;
        }
      }));
      if (cancelled) return;

      const confirmed = settled.filter((result) => result !== null);
      if (confirmed.length === 0) return;
      const confirmedHashes = new Set(confirmed.map((result) => result.transaction.hash));
      updatePendingProposalTransactions((current) =>
        current.filter((transaction) => !confirmedHashes.has(transaction.hash)),
      );
      if (confirmed.some((result) => result.status === "success" && result.transaction.stage === "proposal")) {
        setRefreshNonce((nonce) => nonce + 1);
        toast({ title: "Proposal created", description: "The proposal is confirmed and recorded on Polygon." });
      }
      if (confirmed.some((result) => result.status === "success" && result.transaction.stage === "delegation")) {
        toast({ title: "Voting power activated", description: "Delegation is confirmed. If the proposal dialog did not continue, submit the proposal again." });
      }
      if (confirmed.some((result) => result.status !== "success" && result.transaction.stage === "proposal")) {
        toast({ title: "Proposal failed", description: "A proposal transaction reverted on Polygon.", variant: "destructive" });
      }
      if (confirmed.some((result) => result.status !== "success" && result.transaction.stage === "delegation")) {
        toast({ title: "Delegation failed", description: "The voting-power delegation reverted on Polygon.", variant: "destructive" });
      }
    };

    void checkPendingTransactions();
    const interval = window.setInterval(() => void checkPendingTransactions(), 4_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [pendingProposalTransactions, publicClient, toast, updatePendingProposalTransactions]);

  const loadProposals = useCallback(async () => {
    if (!governorAddress || !publicClient) {
      setProposals([]);
      return;
    }

    setIsLoadingProposals(true);
    setProposalError(null);
    try {
      const latestBlock = await publicClient.getBlockNumber();
      const earliestBlock = getHistoryStartBlock(latestBlock, createdAt);
      const logs: ProposalCreatedLog[] = [];
      const ranges = getDescendingBlockRanges(latestBlock, earliestBlock);

      const logQueryConcurrency = polygonArchiveRpcClients.length;
      for (let index = 0; index < ranges.length && logs.length < 50; index += logQueryConcurrency) {
        const batch = ranges.slice(index, index + logQueryConcurrency);
        const chunks = await Promise.all(batch.map(({ fromBlock, toBlock }, batchIndex) => {
          const logClient = polygonArchiveRpcClients[(index + batchIndex) % logQueryConcurrency];
          return getLogsWithAdaptiveRange(fromBlock, toBlock, (adaptiveFromBlock, adaptiveToBlock) => logClient.getLogs({
            address: governorAddress,
            event: PROPOSAL_CREATED_EVENT,
            fromBlock: adaptiveFromBlock,
            toBlock: adaptiveToBlock,
          }));
        }));
        logs.push(...chunks.flat() as unknown as ProposalCreatedLog[]);
        if (index + logQueryConcurrency < ranges.length && logs.length < 50) {
          await waitForNextLogBatch();
        }
      }

      logs.sort((left, right) => {
        const blockOrder = Number((right.blockNumber ?? 0n) - (left.blockNumber ?? 0n));
        if (blockOrder !== 0) return blockOrder;
        return Number(right.logIndex ?? 0) - Number(left.logIndex ?? 0);
      });

      const loaded = await Promise.all(logs.slice(0, 50).map(async (log): Promise<Proposal> => {
        const proposalId = log.args.proposalId as bigint;
        const [state, votes] = await Promise.all([
          publicClient.readContract({ address: governorAddress, abi: GOVERNOR_ABI, functionName: "state", args: [proposalId] } as never),
          publicClient.readContract({ address: governorAddress, abi: GOVERNOR_ABI, functionName: "proposalVotes", args: [proposalId] } as never),
        ]);
        const stateNumber = Number(state);
        const targets = [...(log.args.targets || [])];
        const values = [...(log.args.values || [])];
        const calldatas = [...(log.args.calldatas || [])];
        if (targets.length === 0 || targets.length !== values.length || targets.length !== calldatas.length) {
          throw new Error(`Proposal ${proposalId.toString()} has incomplete execution data in its creation event.`);
        }

        const [againstVotes, forVotes, abstainVotes] = votes as readonly [bigint, bigint, bigint];
        const needsQueuing = stateNumber === 4
          ? await publicClient.readContract({
              address: governorAddress,
              abi: GOVERNOR_ABI,
              functionName: "proposalNeedsQueuing",
              args: [proposalId],
            } as never) as boolean
          : true;
        const eta = stateNumber === 5
          ? await publicClient.readContract({
              address: governorAddress,
              abi: GOVERNOR_ABI,
              functionName: "proposalEta",
              args: [proposalId],
            } as never) as bigint
          : null;
        return {
          id: proposalId.toString(),
          proposer: String(log.args.proposer),
          description: String(log.args.description || `Proposal #${proposalId.toString()}`),
          state: PROPOSAL_STATES[stateNumber] || "Pending",
          forVotes: forVotes.toString(),
          againstVotes: againstVotes.toString(),
          abstainVotes: abstainVotes.toString(),
          startBlock: String(log.args.voteStart),
          endBlock: String(log.args.voteEnd),
          targets,
          values,
          calldatas,
          needsQueuing,
          eta: eta?.toString() || null,
          executed: stateNumber === 7,
          canceled: stateNumber === 2,
        };
      }));
      setProposals(loaded);
    } catch (error) {
      console.error("Error loading on-chain DAO proposals:", error);
      setProposalError(error instanceof Error ? error.message : "Unable to read proposals from Polygon.");
      setProposals([]);
    } finally {
      setIsLoadingProposals(false);
    }
  }, [createdAt, governorAddress, publicClient]);

  useEffect(() => {
    loadProposals();
  }, [loadProposals, refreshNonce]);

  // Compound-style governance uses delegated votes from the previous block.
  // A token balance (or ERC-20 approval) alone is not voting power.
  const { writeContractAsync: governanceWrite, isPending: isGovernanceWritePending } = useWriteContract();
  const [proposalStep, setProposalStep] = useState("idle");

  const createProposal = async (
    targets: Address[],
    values: bigint[],
    calldatas: `0x${string}`[],
    description: string
  ): Promise<boolean> => {
    if (!governorAddress) {
      toast({
        title: "Error",
        description: "Governor address not set",
        variant: "destructive",
      });
      return false;
    }

    try {
      if (!isConnected || !address) {
        toast({ title: "Connect wallet", description: "Connect your Polygon wallet before creating a proposal.", variant: "destructive" });
        return false;
      }
      if (!(await ensureCorrectNetwork())) {
        toast({ title: "Polygon required", description: "Switch to Polygon before creating the proposal.", variant: "destructive" });
        return false;
      }
      if (!publicClient) throw new Error("Polygon client unavailable");
      setProposalStep("Checking voting power...");
      const proposalTitle = description.split("\n")[0]?.replace(/^#\s*/, "") || "Untitled proposal";

      // The first deployed factory created a placeholder that exposed token()
      // and proposalThreshold() but no Governor lifecycle. Detect it before a
      // wallet prompt instead of surfacing an opaque selector/fallback revert.
      try {
        await publicClient.readContract({
          address: governorAddress,
          abi: GOVERNOR_ABI,
          functionName: "clock",
        } as never);
      } catch (error) {
        if (isContractExecutionRevert(error)) {
          throw new Error("This DAO was deployed with the legacy placeholder governor and cannot create proposals. Deploy a new DAO with the current factory; the existing governor is not upgradeable.");
        }
        throw error;
      }

      const [tokenAddressResult, thresholdResult] = await Promise.all([
        publicClient.readContract({ address: governorAddress, abi: GOVERNOR_ABI, functionName: "token" } as never),
        publicClient.readContract({ address: governorAddress, abi: GOVERNOR_ABI, functionName: "proposalThreshold" } as never),
      ]);
      const tokenAddress = tokenAddressResult as Address;
      const threshold = thresholdResult as bigint;
      const [balance, delegatee, currentVotes] = await Promise.all([
        publicClient.readContract({ address: tokenAddress, abi: GOVERNANCE_TOKEN_ABI, functionName: "balanceOf", args: [address] } as never) as Promise<bigint>,
        publicClient.readContract({ address: tokenAddress, abi: GOVERNANCE_TOKEN_ABI, functionName: "delegates", args: [address] } as never) as Promise<Address>,
        publicClient.readContract({ address: tokenAddress, abi: GOVERNANCE_TOKEN_ABI, functionName: "getVotes", args: [address] } as never) as Promise<bigint>,
      ]);
      if (currentVotes < threshold) {

        if (balance < threshold) {
          toast({
            title: "Not enough proposal voting power",
            description: `This DAO requires ${threshold.toString()} delegated token units, but this wallet holds ${balance.toString()}.`,
            variant: "destructive",
          });
          return false;
        }

        if (delegatee.toLowerCase() !== address.toLowerCase()) {
          toast({
            title: delegatee === ZERO_ADDRESS ? "Activate voting power" : "Delegate voting power to yourself",
            description: "This is not a proposal vote. Confirm the self-delegation transaction to activate your tokens' voting power; the proposal transaction follows after confirmation.",
          });
          setProposalStep("Confirm delegation in wallet...");
          await verifyPolygonWalletChain();
          const delegationHash = await submitWithTimeoutRecovery(
            () => governanceWrite({
              address: tokenAddress,
              abi: GOVERNANCE_TOKEN_ABI,
              functionName: "delegate",
              args: [address],
              chain: polygon,
              account: address,
            } as never),
            "Delegation",
          );
          upsertPendingProposalTransaction({
            hash: delegationHash,
            title: proposalTitle,
            submittedAt: Date.now(),
            stage: "delegation",
          });
          setProposalStep("Waiting for delegation confirmation...");
          const delegationReceipt = await publicClient.waitForTransactionReceipt({ hash: delegationHash, timeout: 60_000 });
          if (delegationReceipt.status !== "success") throw new Error("Voting-power delegation reverted on Polygon.");
          removePendingProposalTransaction(delegationHash);
          setProposalStep("Waiting one Polygon block for voting power...");
          await waitForBlockAfter(publicClient, delegationReceipt.blockNumber);
        } else {
          toast({
            title: "Not enough delegated voting power",
            description: "This wallet is already self-delegated, but its current voting power is below the proposal threshold.",
            variant: "destructive",
          });
          return false;
        }
      }

      // GovernorVotes delegates clock and historical-vote reads to its token.
      // Query the token at `latest`: public free RPCs can incorrectly revert
      // state calls pinned to an explicit recent block as archive requests.
      let initialClock = await publicClient.readContract({
        address: tokenAddress,
        abi: GOVERNANCE_TOKEN_ABI,
        functionName: "clock",
      } as never) as bigint | number;
      let governanceClock = BigInt(initialClock);
      let priorVotes = governanceClock > 0n
        ? await publicClient.readContract({
            address: tokenAddress,
            abi: GOVERNANCE_TOKEN_ABI,
            functionName: "getPastVotes",
            args: [address, governanceClock - 1n],
          } as never) as bigint
        : 0n;

      if (priorVotes < threshold && currentVotes >= threshold) {
        setProposalStep("Waiting one Polygon block for voting power...");
        const currentBlock = await publicClient.getBlockNumber({ cacheTime: 0 });
        await waitForBlockAfter(publicClient, currentBlock);
        initialClock = await publicClient.readContract({
          address: tokenAddress,
          abi: GOVERNANCE_TOKEN_ABI,
          functionName: "clock",
        } as never) as bigint | number;
        governanceClock = BigInt(initialClock);
        priorVotes = await publicClient.readContract({
          address: tokenAddress,
          abi: GOVERNANCE_TOKEN_ABI,
          functionName: "getPastVotes",
          args: [address, governanceClock - 1n],
        } as never) as bigint;
      }

      if (priorVotes < threshold) {
        throw new Error(`Proposal requires ${threshold.toString()} prior-checkpoint votes, but this wallet has ${priorVotes.toString()}. Delegation is not active on Polygon yet.`);
      }

      const descriptionHash = keccak256(toBytes(description));
      // Governor proposal ids are keccak256(abi.encode(...)). Compute this
      // locally because some deployed governor versions do not expose the
      // optional hashProposal public selector.
      const proposalId = BigInt(keccak256(encodeAbiParameters(
        parseAbiParameters("address[], uint256[], bytes[], bytes32"),
        [targets, values, calldatas, descriptionHash],
      )));
      let existingState: number | null = null;
      try {
        existingState = await publicClient.readContract({
          address: governorAddress,
          abi: GOVERNOR_ABI,
          functionName: "state",
          args: [proposalId],
        } as never) as number;
      } catch (error) {
        // Governor.state() reverts for an unknown proposal. Only swallow an
        // actual EVM revert; transport, authentication, and RPC errors remain
        // visible instead of being misclassified as a missing proposal.
        if (!isContractExecutionRevert(error)) throw error;
      }
      if (existingState !== null) {
        setRefreshNonce((nonce) => nonce + 1);
        throw new Error(`This exact proposal already exists on Polygon (${PROPOSAL_STATES[Number(existingState)] || `state ${existingState}`}). Change its description or use the existing proposal.`);
      }

      // Simulate at the provider's latest block. Explicit recent block numbers
      // are not consistently served by free Polygon RPC archive backends.
      await publicClient.simulateContract({
        address: governorAddress,
        abi: GOVERNOR_ABI,
        functionName: "propose",
        args: [targets, values, calldatas, description],
        account: address,
      } as never);

      setProposalStep("Confirm proposal in wallet...");
      await verifyPolygonWalletChain();
      const hash = await submitWithTimeoutRecovery(
        () => governanceWrite({
          address: governorAddress,
          abi: GOVERNOR_ABI,
          functionName: "propose",
          args: [targets, values, calldatas, description],
          chain: polygon,
          account: address,
          gas: PROPOSAL_GAS_LIMIT,
        } as never),
        "Proposal",
      );
      upsertPendingProposalTransaction({ hash, title: proposalTitle, submittedAt: Date.now(), stage: "proposal" });
      toast({
        title: "Proposal transaction submitted",
        description: `Polygon is confirming ${hash.slice(0, 10)}... You can continue using the app.`,
      });

      return true;
    } catch (error) {
      const timedOutHash = getTimedOutTransactionHash(error);
      if (timedOutHash) {
        console.warn("Transaction receipt lookup timed out:", timedOutHash);
        toast({
          title: "Transaction confirmation delayed",
          description: `Transaction ${timedOutHash.slice(0, 10)}... was submitted. Check PolygonScan before retrying this step.`,
        });
        return false;
      }

      console.error("Error creating proposal:", error);
      toast({
        title: "Error",
        description: getErrorMessage(error, "Failed to create proposal"),
        variant: "destructive",
      });
      return false;
    } finally {
      setProposalStep("idle");
    }
  };

  // Vote on proposal
  const { writeContractAsync: voteWrite, data: voteTxHash } = useWriteContract();
  const { isLoading: isVoteLoading, isSuccess: isVoteSuccess } = 
    useWaitForTransactionReceipt({ hash: voteTxHash });

  useEffect(() => {
    if (isVoteSuccess) setRefreshNonce((nonce) => nonce + 1);
  }, [isVoteSuccess]);

  const vote = async (proposalId: string, support: 0 | 1 | 2) => {
    if (!governorAddress) {
      toast({
        title: "Error",
        description: "Governor address not set",
        variant: "destructive",
      });
      return;
    }

    try {
      if (!isConnected || !address) {
        toast({ title: "Connect wallet", description: "Connect your Polygon wallet before voting.", variant: "destructive" });
        return;
      }
      if (!(await ensureCorrectNetwork())) {
        toast({ title: "Polygon required", description: "Switch to Polygon before voting.", variant: "destructive" });
        return;
      }
      await verifyPolygonWalletChain();
      const hash = await voteWrite({
        address: governorAddress,
        abi: GOVERNOR_ABI,
        functionName: "castVote",
        args: [BigInt(proposalId), support],
        chain: polygon,
        account: address,
      } as never);

      const voteType = support === 1 ? "For" : support === 0 ? "Against" : "Abstain";
      toast({
        title: "Vote transaction submitted",
        description: `Your ${voteType} vote is awaiting Polygon confirmation: ${hash.slice(0, 10)}…`,
      });
    } catch (error) {
      console.error("Error voting:", error);
      toast({
        title: "Error",
        description: getErrorMessage(error, "Failed to cast vote"),
        variant: "destructive",
      });
    }
  };

  const { writeContractAsync: lifecycleWrite, isPending: isLifecycleWalletPending } = useWriteContract();
  const [activeLifecycleAction, setActiveLifecycleAction] = useState<ActiveProposalLifecycleAction | null>(null);

  const submitLifecycleAction = async (proposal: Proposal, action: ProposalLifecycleAction) => {
    if (!governorAddress) {
      toast({ title: "Error", description: "Governor address not set", variant: "destructive" });
      return;
    }

    try {
      if (!isConnected || !address) {
        toast({ title: "Connect wallet", description: `Connect your Polygon wallet before you ${action} this proposal.`, variant: "destructive" });
        return;
      }
      if (!(await ensureCorrectNetwork())) {
        toast({ title: "Polygon required", description: `Switch to Polygon before you ${action} this proposal.`, variant: "destructive" });
        return;
      }
      if (!publicClient) throw new Error("Polygon client unavailable");

      const expectedState = action === "queue" ? "Succeeded" : proposal.needsQueuing ? "Queued" : "Succeeded";
      if (proposal.state !== expectedState) {
        throw new Error(`This proposal must be ${expectedState.toLowerCase()} before it can be ${action}d.`);
      }

      const descriptionHash = keccak256(toBytes(proposal.description));
      const args = [proposal.targets, proposal.values, proposal.calldatas, descriptionHash] as const;
      setActiveLifecycleAction({ proposalId: proposal.id, action });

      await publicClient.simulateContract({
        address: governorAddress,
        abi: GOVERNOR_ABI,
        functionName: action,
        args,
        account: address,
      } as never);

      await verifyPolygonWalletChain();
      const hash = await submitWithTimeoutRecovery(
        () => lifecycleWrite({
          address: governorAddress,
          abi: GOVERNOR_ABI,
          functionName: action,
          args,
          chain: polygon,
          account: address,
        } as never),
        action === "queue" ? "Queue proposal" : "Execute proposal",
      );
      toast({
        title: action === "queue" ? "Queue transaction submitted" : "Execution transaction submitted",
        description: `Polygon is confirming ${hash.slice(0, 10)}...`,
      });

      const receipt = await publicClient.waitForTransactionReceipt({ hash, timeout: 60_000 });
      if (receipt.status !== "success") throw new Error(`The proposal ${action} transaction reverted on Polygon.`);
      setRefreshNonce((nonce) => nonce + 1);
      toast({
        title: action === "queue" ? "Proposal queued" : "Proposal executed",
        description: action === "queue"
          ? "The timelock is running. Execute the proposal after its delay ends."
          : "The proposal's actions were executed successfully.",
      });
    } catch (error) {
      const timedOutHash = getTimedOutTransactionHash(error);
      if (timedOutHash) {
        toast({
          title: "Transaction confirmation delayed",
          description: `Transaction ${timedOutHash.slice(0, 10)}... was submitted. Check PolygonScan before retrying.`,
        });
        return;
      }
      console.error(`Error ${action}ing proposal:`, error);
      toast({
        title: action === "queue" ? "Unable to queue proposal" : "Unable to execute proposal",
        description: getErrorMessage(error, `Failed to ${action} proposal`),
        variant: "destructive",
      });
    } finally {
      setActiveLifecycleAction(null);
    }
  };

  const queueProposal = (proposal: Proposal) => submitLifecycleAction(proposal, "queue");
  const executeProposal = (proposal: Proposal) => submitLifecycleAction(proposal, "execute");

  return {
    proposals,
    pendingProposalTransactions,
    dismissPendingProposal: removePendingProposalTransaction,
    isLoadingProposals,
    createProposal,
    isProposeLoading: isGovernanceWritePending || proposalStep !== "idle",
    proposalStatus: proposalStep === "idle" ? undefined : proposalStep,
    vote,
    isVoteLoading,
    isVoteSuccess,
    queueProposal,
    executeProposal,
    activeLifecycleAction,
    isLifecycleLoading: isLifecycleWalletPending || activeLifecycleAction !== null,
    proposalCount: proposals.length,
    proposalError,
  };
};
